import {
  getListPlatformsBiomeshPlatformListGetQueryKey,
  useListPlatformsBiomeshPlatformListGet,
  useUpdatePlatformBiomeshPlatformPlatformIdPut,
} from "@phc/common/gen/threatAnalyticsGen"
import { useQueryClient } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import { useCallback, useMemo, useState } from "react"
import { toCentroid } from "../Cop/copUtils"

/**
 * Manages the node drawer: which nodes to show (mapped selection vs unmapped list),
 * and the place-on-map interaction for unmapped nodes.
 */
export function useNodeDrawer() {
  const [mappedNodeIds, setMappedNodeIds] = useState<string[]>([])
  const [showUnmapped, setShowUnmapped] = useState(false)
  const [placingNodeId, setPlacingNodeId] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const { data: allPlatforms } = useListPlatformsBiomeshPlatformListGet()
  const { mutateAsync: updatePlatform } =
    useUpdatePlatformBiomeshPlatformPlatformIdPut()

  const unmappedNodeIds = useMemo(
    () => allPlatforms?.filter(p => !p.centroid).map(p => p.platform_id) ?? [],
    [allPlatforms]
  )

  const isOpen = mappedNodeIds.length > 0 || showUnmapped
  const nodeIds = showUnmapped ? unmappedNodeIds : mappedNodeIds

  const placingNodeName = useMemo(
    () =>
      placingNodeId
        ? (allPlatforms?.find(p => p.platform_id === placingNodeId)?.name ??
          null)
        : null,
    [placingNodeId, allPlatforms]
  )

  const openNodes = useCallback((ids: string[]) => {
    setShowUnmapped(false)
    setPlacingNodeId(null)
    setMappedNodeIds(ids)
  }, [])

  const toggleUnmapped = useCallback(() => {
    setShowUnmapped(prev => {
      if (!prev) setMappedNodeIds([])
      return !prev
    })
  }, [])

  const close = useCallback(() => {
    setMappedNodeIds([])
    setShowUnmapped(false)
    setPlacingNodeId(null)
  }, [])

  const placeNode = useCallback(
    async (lngLat: { lng: number; lat: number }) => {
      if (!placingNodeId) return
      const platform = allPlatforms?.find(p => p.platform_id === placingNodeId)
      if (!platform) return
      try {
        await updatePlatform({
          platformId: placingNodeId,
          data: {
            name: platform.name,
            platform_type: platform.platform_type,
            centroid: toCentroid(lngLat.lat, lngLat.lng),
            operation_ids:
              platform.operations
                ?.map(o => o.operation_id)
                .filter((id): id is string => !!id) ?? undefined,
          },
        })
        await queryClient.invalidateQueries({
          queryKey: getListPlatformsBiomeshPlatformListGetQueryKey(),
        })
        enqueueSnackbar(`"${platform.name}" placed on map`, {
          variant: "success",
        })
        setPlacingNodeId(null)
        setShowUnmapped(false)
        setMappedNodeIds([placingNodeId])
      } catch {
        enqueueSnackbar("Failed to place node", { variant: "error" })
      }
    },
    [placingNodeId, allPlatforms, updatePlatform, queryClient]
  )

  return {
    isOpen,
    nodeIds,
    isUnmapped: showUnmapped,
    openNodes,
    toggleUnmapped,
    close,
    placingNodeId,
    placingNodeName,
    startPlacing: setPlacingNodeId,
    cancelPlacing: useCallback(() => setPlacingNodeId(null), []),
    placeNode,
  }
}
